/**
 * Centralized date utilities for timezone-agnostic date handling.
 *
 * Philosophy: dates and times are treated as literal wall-clock values.
 * The backend value is what the user should see, regardless of the device timezone.
 */

type ParsedBackendDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  hasTime: boolean;
};

const BACKEND_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

const LONG_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
});

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'UTC',
});

function parseBackendDateParts(dateStr: string | null | undefined): ParsedBackendDateParts | null {
  if (!dateStr) return null;

  const trimmed = dateStr.trim();
  const match = trimmed.match(BACKEND_DATE_PATTERN);
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match;

  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: hour ? Number(hour) : 0,
    minute: minute ? Number(minute) : 0,
    second: second ? Number(second) : 0,
    hasTime: hour !== undefined && minute !== undefined,
  };
}

function toUtcDate(parts: ParsedBackendDateParts, includeTime = parts.hasTime): Date {
  return new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      includeTime ? parts.hour : 0,
      includeTime ? parts.minute : 0,
      includeTime ? parts.second : 0,
    ),
  );
}

function formatLongDate(parts: ParsedBackendDateParts): string {
  return LONG_DATE_FORMATTER.format(toUtcDate(parts, false));
}

function formatShortDate(parts: ParsedBackendDateParts): string {
  return SHORT_DATE_FORMATTER.format(toUtcDate(parts, false));
}

function formatTime(parts: ParsedBackendDateParts): string {
  return TIME_FORMATTER.format(toUtcDate(parts, true));
}

function formatSingleDisplayValue(parts: ParsedBackendDateParts): string {
  if (!parts.hasTime) {
    return formatLongDate(parts);
  }

  return `${formatLongDate(parts)}, ${formatTime(parts)}`;
}

function getDateKey(parts: ParsedBackendDateParts): string {
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

function getTimeKey(parts: ParsedBackendDateParts): string {
  if (!parts.hasTime) return '';

  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}:${String(parts.second).padStart(2, '0')}`;
}

/**
 * Converts a Date to YYYY-MM-DD string for HTML date inputs.
 * The date is treated as absolute (no timezone conversion).
 */
export function toDateInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * Converts a Date to YYYY-MM-DDTHH:MM string for HTML datetime-local inputs.
 * The datetime is treated as absolute (no timezone conversion).
 */
export function toDateTimeInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

/**
 * Converts a Date to ISO string without milliseconds (YYYY-MM-DDTHH:MM:SS).
 * Useful for API calls where full precision isn't needed.
 */
export function toISOStringNoMs(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().split('.')[0];
}

/**
 * Formats a backend date string for display without timezone conversion.
 * Returns format: "January 5, 2026"
 */
export function formatDisplayDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';

  const parts = parseBackendDateParts(dateStr);
  return parts ? formatLongDate(parts) : dateStr;
}

/**
 * Formats a backend date string for short display without timezone conversion.
 * Returns format: "1/5/2026"
 */
export function formatDisplayDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';

  const parts = parseBackendDateParts(dateStr);
  return parts ? formatShortDate(parts) : dateStr;
}

/**
 * Formats a backend datetime string for display without timezone conversion.
 * Returns format: "January 5, 2026, 2:30 PM"
 */
export function formatDisplayDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';

  const parts = parseBackendDateParts(dateStr);
  return parts ? formatSingleDisplayValue(parts) : dateStr;
}

/**
 * Parses a date input value (YYYY-MM-DD) as a Date object.
 * Treats the input as absolute (no timezone conversion).
 */
export function parseDateInput(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Parses a datetime-local input value (YYYY-MM-DDTHH:MM) as a Date object.
 * Treats the input as absolute (no timezone conversion).
 */
export function parseDateTimeInput(value: string): Date {
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

/**
 * Gets today's date range (start of day to end of day) as ISO strings.
 * Useful for filtering jobs/records for "today".
 */
export function getTodayRange(): { from: string; to: string } {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  return {
    from: toISOStringNoMs(start),
    to: toISOStringNoMs(end),
  };
}

/**
 * Formats a start/end backend datetime range for display.
 *
 * Rules:
 * - If both missing -> "N/A"
 * - If only one value -> show that single datetime
 * - If same day & same time -> single datetime
 * - If same day & different times -> one date with "start - end" times
 * - If different days -> "start - end" full datetimes
 */
export function formatDateRangeDisplay(start?: string | null, end?: string | null): string {
  if (!start && !end) return 'N/A';

  const startParts = parseBackendDateParts(start);
  const endParts = parseBackendDateParts(end);

  if (!startParts || !endParts) {
    if (startParts || endParts) {
      return formatSingleDisplayValue((startParts || endParts)!);
    }

    if (start && end) {
      return `${start} - ${end}`;
    }

    return start || end || 'N/A';
  }

  const sameDay = getDateKey(startParts) === getDateKey(endParts);
  const sameTime = getTimeKey(startParts) === getTimeKey(endParts);

  if (sameDay && sameTime) {
    return formatSingleDisplayValue(startParts);
  }

  if (sameDay && startParts.hasTime && endParts.hasTime) {
    const dateLabel = formatLongDate(startParts);
    const startTime = formatTime(startParts);
    const endTime = formatTime(endParts);
    return `${dateLabel}, ${startTime} - ${endTime}`;
  }

  return `${formatSingleDisplayValue(startParts)} - ${formatSingleDisplayValue(endParts)}`;
}

const COMPANY_DT_DISPLAY: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/**
 * Parses API date/time strings into a JavaScript Date (UTC instant).
 *
 * Backend DTOs often send UTC as ISO-8601 **without** a `Z` suffix (e.g. `2026-04-08T12:23:00`).
 * `new Date` would interpret that as **local** time, shifting the instant and breaking
 * company-timezone display for users outside UTC.
 *
 * Rule: if the string looks like an ISO datetime and has no zone, treat it as UTC
 * (same idea as {@link PaymentTransactionList}).
 */
export function parseBackendInstantToUtcDate(dateStr: string): Date {
  const s = dateStr.trim();
  if (!s) return new Date(NaN);
  if (/Z$/i.test(s)) return new Date(s);
  // Ends with numeric offset, e.g. +00:00, -04:00, +0530
  if (/[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  // ISO datetime without zone → UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    return new Date(`${s}Z`);
  }
  return new Date(s);
}

/**
 * True when a balance-settled timestamp is the pre-feature sentinel (0001-01-01), meaning the job
 * was completed before technician-balance settlement existed. Callers show an explanatory label
 * instead of a real date. Null/empty (unsettled) → false.
 */
export function isPreFeatureSettleDate(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const d = parseBackendInstantToUtcDate(dateStr);
  return !isNaN(d.getTime()) && d.getUTCFullYear() <= 1;
}

/**
 * True when start & end are both present and represent different instants.
 * Used to decide whether the job schedule editor should show a single
 * date+time field (start == end) or the expanded Start/End range.
 */
export function isScheduleRange(start?: string | null, end?: string | null): boolean {
  if (!start || !end) return false;
  return parseBackendInstantToUtcDate(start).getTime() !== parseBackendInstantToUtcDate(end).getTime();
}

/**
 * Returns an ISO string suitable for `dayjs()` / `new Date()` when the API omits `Z` on UTC datetimes.
 * Empty/null stays empty.
 */
export function normalizeBackendUtcIsoString(dateStr: string | null | undefined): string {
  if (dateStr == null || dateStr === '') return '';
  const s = String(dateStr).trim();
  if (!s) return '';
  if (/Z$/i.test(s)) return s;
  if (/[+-]\d{2}:?\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return `${s}Z`;
  return s;
}

/**
 * Formats a parseable instant (ISO, etc.) for display in the company IANA timezone when set;
 * otherwise uses the browser's local timezone (same idea as `Date` + local `Intl`).
 */
export function formatInstantInCompanyTimeZone(
  dateStr: string | null | undefined,
  timeZone: string | null | undefined,
): string {
  if (!dateStr) return 'N/A';
  const d = parseBackendInstantToUtcDate(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  const tz = timeZone?.trim();
  if (tz) {
    return new Intl.DateTimeFormat('en-US', { ...COMPANY_DT_DISPLAY, timeZone: tz }).format(d);
  }
  return new Intl.DateTimeFormat('en-US', COMPANY_DT_DISPLAY).format(d);
}

/**
 * "Sep 14, 9:05 AM · 2h 15m ago" — shown under the ASAP badge from the job's scheduled date (for an
 * ASAP job, when it came in or was marked ASAP), so a dispatcher can see how long the customer has
 * been waiting.
 * ponytail: computed at render, not ticking; it refreshes whenever the job list refetches.
 */
export function formatAsapWaiting(since: string | null | undefined, timeZone?: string): string {
  if (!since) return '';
  const start = parseBackendInstantToUtcDate(since);
  if (isNaN(start.getTime())) return '';

  const totalMin = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const minutes = totalMin % 60;
  const waited =
    totalMin < 1 ? 'just now'
      : days > 0 ? `${days}d ${hours}h ago`
        : hours > 0 ? `${hours}h ${minutes}m ago`
          : `${minutes}m ago`;

  return `${formatInstantInCompanyTimeZone(since, timeZone)} · ${waited}`;
}

/**
 * Calendar date only (no time), in company timezone when set; otherwise local.
 */
export function formatDateOnlyInCompanyTimeZone(
  dateStr: string | null | undefined,
  timeZone: string | null | undefined,
): string {
  if (!dateStr) return '';
  const raw = dateStr.trim();
  // Always extract just the date portion (YYYY-MM-DD) to avoid off-by-one in any UTC offset.
  // Using noon UTC ensures the date is stable across UTC-12 to UTC+14.
  const datePart = raw.slice(0, 10);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? new Date(datePart + 'T12:00:00Z') : new Date(raw);
  if (isNaN(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const tz = timeZone?.trim();
  if (tz) {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: tz }).format(d);
  }
  return new Intl.DateTimeFormat('en-US', opts).format(d);
}

/**
 * Interprets values as instants and formats date/time parts in the company timezone
 * when `timeZone` is set; otherwise uses the local browser timezone.
 */
export function formatDateTimeRangeInCompanyTimeZone(
  start?: string | null,
  end?: string | null,
  timeZone?: string | null,
  { omitYear = false }: { omitYear?: boolean } = {},
): string {
  const tz = timeZone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!start && !end) return 'N/A';
  const year = omitYear ? undefined : 'numeric';

  const s = start ? parseBackendInstantToUtcDate(start) : null;
  const e = end ? parseBackendInstantToUtcDate(end) : null;
  if (start && (!s || isNaN(s.getTime()))) return formatDateRangeDisplay(start, end);
  if (end && (!e || isNaN(e.getTime()))) return formatDateRangeDisplay(start, end);

  const fmtFull = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: 'long',
      day: 'numeric',
      year,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);

  const fmtDateOnly = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: 'long',
      day: 'numeric',
      year,
    }).format(d);

  const fmtTime = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);

  const dayKey = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

  const timeKey = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(d);

  if (!s && e) return fmtFull(e);
  if (s && !e) return fmtFull(s);
  if (s && e) {
    const sameDay = dayKey(s) === dayKey(e);
    const sameTime = timeKey(s) === timeKey(e);
    if (sameDay && sameTime) return fmtFull(s);
    if (sameDay) return `${fmtDateOnly(s)}, ${fmtTime(s)} - ${fmtTime(e)}`;
    return `${fmtFull(s)} - ${fmtFull(e)}`;
  }
  return 'N/A';
}

// ---------------------------------------------------------------------------
// Company-timezone "today" / date-range helpers
// ---------------------------------------------------------------------------

function resolveTimezone(tz?: string | null): string {
  return tz?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Returns the YYYY-MM-DD string for "today" in the given timezone.
 */
export function getTodayStringInTz(tz?: string | null): string {
  return toDateInputValueInTz(new Date(), tz);
}

/**
 * Checks whether a Date falls on "today" in the given timezone.
 */
export function isTodayInTimeZone(date: Date, tz?: string | null): boolean {
  return toDateInputValueInTz(date, tz) === toDateInputValueInTz(new Date(), tz);
}

/**
 * Returns the current wall-clock "now" as a Date in the given timezone.
 * The returned Date is a regular JS Date whose UTC fields represent the wall-clock
 * values in the given timezone (useful for feeding date-only comparisons).
 */
export function getNowInCompanyTimeZone(tz?: string | null): Date {
  const effectiveTz = resolveTimezone(tz);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: effectiveTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');
  return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
}

/**
 * Returns start/end ISO strings for "today" in the given timezone.
 */
export function getTodayRangeInCompanyTimeZone(tz?: string | null): { from: string; to: string } {
  const now = getNowInCompanyTimeZone(tz);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return {
    from: toISOStringNoMs(start),
    to: toISOStringNoMs(end),
  };
}

/**
 * Returns start/end ISO strings for "this week" (Monday-Sunday) in the given timezone.
 */
export function getThisWeekRangeInCompanyTimeZone(tz?: string | null): { from: string; to: string } {
  const now = getNowInCompanyTimeZone(tz);
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return {
    from: toISOStringNoMs(monday),
    to: toISOStringNoMs(sunday),
  };
}

/**
 * Short date format (e.g. "1/5/2026") in company timezone.
 */
export function formatShortDateInCompanyTimeZone(
  dateStr: string | null | undefined,
  tz?: string | null,
): string {
  if (!dateStr) return 'N/A';
  const raw = dateStr.trim();
  const datePart = raw.slice(0, 10);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? new Date(datePart + 'T12:00:00Z') : new Date(raw);
  if (isNaN(d.getTime())) return String(dateStr);
  const effectiveTz = resolveTimezone(tz);
  return new Intl.DateTimeFormat('en-US', { timeZone: effectiveTz }).format(d);
}

/**
 * Time-only format (e.g. "2:30 PM") in company timezone.
 */
export function formatTimeInCompanyTimeZone(
  dateStr: string | null | undefined,
  tz?: string | null,
): string {
  if (!dateStr) return '';
  const d = parseBackendInstantToUtcDate(dateStr);
  if (isNaN(d.getTime())) return '';
  const effectiveTz = resolveTimezone(tz);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: effectiveTz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/** Shift a `YYYY-MM-DD` string by whole calendar days (pure date math, no timezone involved). */
export function addDaysToDateInputValue(ymd: string, days: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Produces `YYYY-MM-DD` for HTML date inputs using the given timezone.
 */
export function toDateInputValueInTz(date: Date, tz?: string | null): string {
  const effectiveTz = resolveTimezone(tz);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: effectiveTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

/**
 * Produces `YYYY-MM-DDTHH:MM` for HTML datetime-local inputs using the given timezone.
 */
export function toDateTimeInputValueInTz(date: Date, tz?: string | null): string {
  const effectiveTz = resolveTimezone(tz);
  const d = new Intl.DateTimeFormat('en-CA', {
    timeZone: effectiveTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => d.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Converts an HTML datetime-local wall clock in a company timezone to a UTC ISO instant. */
export function dateTimeInputInTimeZoneToIso(value: string, tz?: string | null): string {
  if (!value) throw new Error('Date and time are required');
  if (!tz) return new Date(value).toISOString();
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  const desiredWallClock = Date.UTC(year, month - 1, day, hour, minute, 0);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  const wallClockAt = (instant: number) => {
    const parts = formatter.formatToParts(new Date(instant));
    const part = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
    return Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  };
  let instant = desiredWallClock + (desiredWallClock - wallClockAt(desiredWallClock));
  instant += desiredWallClock - wallClockAt(instant);
  return new Date(instant).toISOString();
}

/**
 * Returns the GMT offset label for a timezone (e.g. "GMT-5", "GMT+5:30").
 */
export function getTimezoneGmtOffset(tz?: string | null): string {
  const effectiveTz = resolveTimezone(tz);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: effectiveTz,
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date());
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value ?? effectiveTz;
}

/**
 * Returns fractional hours (hour + minutes/60) for a Date or ISO string,
 * interpreted in the given timezone.  Falls back to the browser's local
 * timezone when `tz` is not provided.
 *
 * This is the timezone-safe replacement for `date.getHours() + date.getMinutes() / 60`
 * and should be used anywhere a calendar needs to *position* an event vertically.
 */
export function getHoursInTimezone(dateOrStr: Date | string, tz?: string | null): number {
  const d = typeof dateOrStr === 'string' ? parseBackendInstantToUtcDate(dateOrStr) : dateOrStr;
  if (isNaN(d.getTime())) return 0;
  const effectiveTz = resolveTimezone(tz);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: effectiveTz,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const hour = Number(parts.find(p => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find(p => p.type === 'minute')?.value ?? '0');
  return hour + minute / 60;
}

/**
 * Returns a stable date-key string (matching `Date.toDateString()` format) for
 * the wall-clock date of `dateOrStr` in the given timezone.
 *
 * Used to *group* or *bucket* jobs into the correct calendar day when the user's
 * browser timezone differs from the company timezone.
 */
export function getDateKeyInTimezone(dateOrStr: Date | string, tz?: string | null): string {
  const d = typeof dateOrStr === 'string' ? parseBackendInstantToUtcDate(dateOrStr) : dateOrStr;
  if (isNaN(d.getTime())) return '';
  const effectiveTz = resolveTimezone(tz);
  // Build a Date whose *local* fields match the wall-clock in the target TZ,
  // then call toDateString() so it matches the keys generated from calendar grid dates.
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: effectiveTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type: string) => Number(p.find(pt => pt.type === type)?.value ?? '0');
  return new Date(get('year'), get('month') - 1, get('day')).toDateString();
}

/**
 * Returns a JS Date object whose local fields (getFullYear, getHours, etc.) 
 * match the wall-clock time in the given timezone. This allows UI components
 * to treat the resulting Date as if the browser was in that timezone.
 */
export function getCompanyTimeDate(dateOrStr: Date | string, tz?: string | null): Date {
  const d = typeof dateOrStr === 'string' ? parseBackendInstantToUtcDate(dateOrStr) : dateOrStr;
  if (isNaN(d.getTime())) return new Date(NaN);
  
  const effectiveTz = resolveTimezone(tz);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: effectiveTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');
  return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
}

// ---------------------------------------------------------------------------
// VoIP `X-Timezone` header (whole hours from company IANA zone; see DispatchVoip int.TryParse)
// ---------------------------------------------------------------------------

/** Persisted JSON from GlobalCompanyProvider: `{ id, timezone? }`. */
export const SELECTED_COMPANY_DATA_STORAGE_KEY = 'selectedCompanyData';

function readPersistedCompanyTimezoneFromLocalStorage(): string | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(SELECTED_COMPANY_DATA_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || !('timezone' in parsed)) return undefined;
    const tz = (parsed as { timezone?: unknown }).timezone;
    if (typeof tz !== 'string') return undefined;
    const trimmed = tz.trim();
    return trimmed || undefined;
  } catch {
    return undefined;
  }
}

/** Same convention as `Date.prototype.getTimezoneOffset()` but for an IANA zone at `date`. */
function getTimezoneOffsetMinutesForIana(timeZone: string, date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
  const y = Number(pick('year'));
  const mo = Number(pick('month')) - 1;
  const d = Number(pick('day'));
  const h = Number(pick('hour'));
  const min = Number(pick('minute'));
  const s = Number(pick('second'));
  if ([y, mo, d, h, min, s].some((n) => Number.isNaN(n))) {
    throw new Error('Invalid IANA timezone parts');
  }
  const asUtc = Date.UTC(y, mo, d, h, min, s);
  return (date.getTime() - asUtc) / 60000;
}

export interface VoipXTimezoneHeaderOptions {
  /** In-memory IANA zone from app state (required on Capacitor). */
  companyIana?: string | null;
  /** Reference instant for DST; default now. */
  at?: Date;
}

/**
 * Integer string for VoIP `X-Timezone` (e.g. "-5"), aligned with legacy `getTimezoneOffset` rounding.
 */
export function getVoipXTimezoneHeaderValue(options: VoipXTimezoneHeaderOptions = {}): string {
  const at = options.at ?? new Date();
  const iana =
    (options.companyIana && options.companyIana.trim()) || readPersistedCompanyTimezoneFromLocalStorage();
  if (iana) {
    try {
      const offsetMinutes = getTimezoneOffsetMinutesForIana(iana, at);
      const offsetHours = Math.round(-offsetMinutes / 60);
      return `${offsetHours}`;
    } catch {
      // invalid IANA or unsupported environment
    }
  }
  const offsetMinutes = at.getTimezoneOffset();
  const offsetHours = Math.round(-offsetMinutes / 60);
  return `${offsetHours}`;
}
